import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { EndpointService } from '@app/core';
import {
  TablaMaestra,
  InformacionTablaMaestra,
  ValoresEspecificos,
  FiltroTableMasterGeneral,
  EstructuraEspecifica,
  Valores,
  RolUsers,
  Empresa,
  Cargo,
  Login,
  Sede,
  userLogin
} from '@app/shared';
import { resolve } from 'url';
import { reject } from 'lodash';

@Injectable()
export class SystemUsuersDataService{
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) { }

  /**
   * Consulta la lista de tablas maestras del sistema
   *
   * @returns {Observable<TablaMaestra[]>}
   * @memberof TeacherDataService
   */
  getMasterTables(): Observable<TablaMaestra[]> {
    return new Observable(obs => {
      this.http.get<TablaMaestra[]>(this.api.get('masterTable')).subscribe(
        masterTables => {
          obs.next(masterTables);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Consulta la informacion de las tablas maestras
   *
   * @param {number} idMaster
   * @returns {Observable<InformacionTablaMaestra>}
   * @memberof TeacherDataService
   */
  getInformacionMasterTable(idMaster: number, skip: boolean = false): Promise<InformacionTablaMaestra> {
    return new Promise((resolve, reject) => {
      this.http.get<InformacionTablaMaestra>(this.api.get('masterTable', [idMaster])).subscribe(
          masterTable => {
            if (!skip) {
              if (masterTable.estructura.length) {
                this.toastr.success(`${masterTable.valores.length} registros consultados.`, 'Operación exitosa');
              } else {
                this.toastr.warning('Agregue una estructura a la tabla para poder consultar los registros',
                'Tabla maestra incompleta');
              }
            }
            resolve(masterTable);
          },
          err => {
            reject(err);
          }
        );
    });
  }

   /**
   * Consulta la informacion de usuarios del sistema con las tablas maestras
   *
   * @param {number} idMaster
   * @returns {Observable<InformacionTablaMaestra>}
   * @memberof TeacherDataService
   */
    getInformacionUserMasterTable(skip: boolean = false): Promise<InformacionTablaMaestra> {
      return new Promise((resolve, reject) => {
        this.http.get<InformacionTablaMaestra>(this.api.get('adminUsers')).subscribe(
            masterTable => {
              if (!skip) {
                if (masterTable.estructura.length) {
                  this.toastr.success(`${masterTable.valores.length} registros consultados.`, 'Operación exitosa');
                } else {
                  this.toastr.warning('Agregue una estructura a la tabla para poder consultar los registros',
                  'Tabla maestra incompleta');
                }
              }
              resolve(masterTable);
            },
            err => {
              reject(err);
            }
          );
      });
    }

     /**
   * Consulta la informacion de usuarios del sistema sin login con las tablas maestras
   *
   * @param {number} idMaster
   * @returns {Observable<InformacionTablaMaestra>}
   * @memberof TeacherDataService
   */
      getInformacionUserWithoutLoginMasterTable(skip: boolean = false): Promise<InformacionTablaMaestra> {
        return new Promise((resolve, reject) => {
          this.http.get<InformacionTablaMaestra>(this.api.get('adminUsersWithoutLogin')).subscribe(
              masterTable => {
                if (!skip) {
                  if (masterTable.estructura.length) {
                    this.toastr.success(`${masterTable.valores.length} registros consultados.`, 'Operación exitosa');
                  } else {
                    this.toastr.warning('Agregue una estructura a la tabla para poder consultar los registros',
                    'Tabla maestra incompleta');
                  }
                }
                resolve(masterTable);
              },
              err => {
                reject(err);
              }
            );
        });
      }

  /**
   * Consulta de los valores de una maestra
   *
   * @param {EstructuraEspecifica} structure
   * @param {FiltroTableMasterGeneral[]} [filterMaster]
   * @returns {Promise<ValoresEspecificos[]>}
   * @memberof TeacherDataService
   */
  getColumnsSpecificMasterTable(
    structure: EstructuraEspecifica,
    filterMaster?: FiltroTableMasterGeneral[],
    idRegistry?: string
  ): Promise<ValoresEspecificos[]> {
    const formData = {
      estructura: structure
    };

    if (filterMaster && filterMaster.length) {
      formData['condicion'] = filterMaster;
    }

    if (idRegistry) {
      formData['id_registro'] = idRegistry;
    }

    return new Promise((resolve, reject) => {
      this.http.post<ValoresEspecificos[]>(this.api.get('masterTableSpecificInformation'), formData).subscribe(
          specificValues => {
            resolve(specificValues);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Realiza las acciones correspondientes a los registros de las tablas maestras
   *
   * @param {number} idTableMaster
   * @param {*} data
   * @param {('delete' | 'new' | 'update')} [action='delete']
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  setInformationDynamicTableMaster(
    idTableMaster: number,
    data: any,
    action: 'delete' | 'new' | 'update' = 'delete'
  ): Observable<boolean> {
    const dataForm: Object = new Object();
    switch (action) {
      case 'new':
        dataForm['crear'] = [];
        dataForm['crear'].push(data);
        break;
      case 'update':
        dataForm['modificar'] = [];
        dataForm['modificar'].push(data);
        break;
      default:
        dataForm['eliminar'] = [];
        dataForm['eliminar'].push(data);
        break;
    }

    return new Observable(obs => {
      this.http
        .put(this.api.get('masterTableInformation', [idTableMaster]), dataForm, {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            this.toastr.success('Registro guardado correctamente.', 'Operación exitosa');
          },
          err => {
            obs.next(false);
            obs.error(err);
          }
        );
    });
  }

  /**
   * Agrega cada registro de la tabla maestra dinamica
   *
   * @param {number} idTableMaster
   * @param {*} data
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  addInformationDynamicTableMaster(
    idTableMaster: number,
    data: any,
  ): Observable<boolean> {
    const dataForm = {
      id_maestra: idTableMaster,
      datos: data
    };

    return new Observable(obs => {
      this.http.skipErrorHandler()
        .post(this.api.get('masterTableInformation'), dataForm, {
          observe: 'response'
        })
        .subscribe(
          res => {
            this.toastr.success('Registro guardado correctamente.', 'Operación exitosa');
            obs.next(true);
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              let message = '';
              if (typeof err.error === 'object') {
                for (const key in err.error) {
                  if (key) {
                    message += key + ': ' + err.error[key];
                  }
                }
              }
              if (message) {
                this.toastr.error(message, 'Error de operación');
              } else {
                this.toastr.error('El registro no se pudo guardar.', 'Error de operación');
              }
            }
            obs.next(false);
            obs.error(err);
          }
        );
    });
  }

  /**
   * Elimina un registro de la tabla maestra dinamica
   *
   * @param {number} idRecord
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  deleteInformationDynamicTableMaster(
    idRecord: number
  ): Observable<boolean> {

    return new Observable(obs => {
      this.http.skipErrorHandler()
        .delete(this.api.get('masterTableInformation', [idRecord]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            this.toastr.success('Registro Elimiando correctamente.', 'Operación exitosa');
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              if (err.error['in_use']) {
                this.toastr.error(err.error['in_use'], 'Error de operación');
              } else {
                this.toastr.error('El registro no se pudo eliminar.', 'Error de operación');
              }
            }
            obs.next(false);
            obs.error(err);
          }
        );
    });
  }

  /**
   * Actualiza un registro de la tabla maestra dinamica
   *
   * @param {number} idRecord
   * @param {*} data
   * @param {boolean} skipAlert
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  updateInformationDynamicTableMaster(
    idRecord: number,
    data: any,
    skipAlert: boolean = false
  ): Observable<boolean> {

    let dataForm: {};

    if (!data['userLogin']) {
      dataForm = {
        datos: data
      };
    } else {
      dataForm = {
        user_login: data['userLogin']
      };
    }

    return new Observable(obs => {
      this.http.skipErrorHandler()
        .put(this.api.get('masterTableInformation', [idRecord]), dataForm, {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            if (!skipAlert) {
              this.toastr.success('Registro Aactualizado correctamente.', 'Operación exitosa');
            }
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              let message = '';
              if (typeof err.error === 'object') {
                for (const key in err.error) {
                  if (key) {
                    message += key + ': ' + err.error[key];
                  }
                }
              }
              if (message) {
                this.toastr.error(message, 'Error de operación');
              } else {
                this.toastr.error('El registro no se pudo guardar.', 'Error de operación');
              }
            }
            obs.next(false);
            obs.error(err);
          }
        );
    });
  }

  /**
   * Agregar tabla maestra
   *
   * @param {TablaMaestra} data
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  addMasterTable(data: TablaMaestra): Observable<boolean> {
    return new Observable(obs => {
      this.http.skipErrorHandler()
        .post(this.api.get('masterTable'), data, {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            this.toastr.success('Maestra agregada.', 'Operación exitosa');
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.error(err.error.menssage, 'Error de operacion');
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Actualizar tabla maestra
   *
   * @param {number} idMasterTable
   * @param {TablaMaestra} data
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  updateMasterTable(idMasterTable: number, data: TablaMaestra): Observable<boolean> {
    return new Observable(obs => {
      this.http
        .put(this.api.get('masterTable', [idMasterTable]), data, {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            this.toastr.success('Maestra actualizada.', 'Operación exitosa');
          },
          err => {
            obs.error(err);
          }
        );
    });
  }

  /**
   * Eliminar tabla maestra
   *
   * @param {number} idMasterTable
   * @returns {Observable<boolean>}
   * @memberof TeacherDataService
   */
  deleteMasterTable(idMasterTable: number): Observable<boolean> {
    return new Observable(obs => {
      this.http.skipErrorHandler()
        .delete(this.api.get('masterTable', [idMasterTable]), {
          observe: 'response'
        })
        .subscribe(
          res => {
            obs.next(true);
            this.toastr.success('Maestra eliminada.', 'Operación exitosa');
          },
          err => {
            if (err.status !== 401 && err.status !== 403) {
              this.toastr.warning(err.error.menssage, 'Acción no permitida');
            }
            obs.error(err);
          }
        );
    });
  }

  /**
   * Consulta un registro en especifico
   *
   * @param {number} idRecord
   * @returns {Promise<Valores>}
   * @memberof TeacherDataService
   */
  getInformationDynamicTableMaster(
    idRecord: number,
  ): Promise<Valores> {

    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler()
        .get<Valores>(this.api.get('masterTableInformation', [idRecord]))
        .subscribe(
          valor => {
            resolve(valor);
          },
          err => {
            let message = '';
            for (const key in err.error) {
              if (key) {
                message += key + ': ' + err.error[key];
              }
            }
            if (message) {
              this.toastr.error(message, 'Error de operación');
            } else {
              this.toastr.error('Ha ocurrido un error inesperado.', 'Error de operación');
            }
            reject(err);
          }
        );
    });
  }

   /**
    *Retorna los grupos del sistema
    *
    * @return {*}  {Observable<RolUsers[]>}
    * @memberof SystemUsuersDataService
    */
   getRoles(): Observable<RolUsers[]> {
    return new Observable(obs => {
      this.http.skipErrorHandler()
       .get<RolUsers[]>(this.api.get('roles')).subscribe(
        roles => {
          obs.next(roles);
        },
        err => {
          this.toastr.error('Lo sentimos ha ocurrido un error', 'Error de operación');
          obs.error(err);
        }
      );
    });
  }
  
  /**
  *Consulta las empresas del sistema
  *
  * @return {*}  {Observable<Empresa[]>}
  * @memberof SystemUsuersDataService
  */
   getCompanies(): Observable<Empresa[]> {
    return new Observable(obs => {
      this.http.get<Empresa[]>(this.api.get('company')).subscribe(
        companies => {
          obs.next(companies);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  
  /**
  *Consulta los cargos del sistema
  *
  * @return {*}  {Observable<Cargo[]>}
  * @memberof SystemUsuersDataService
  */
   getCharges(): Observable<Cargo[]> {
    return new Observable(obs => {
      this.http.get<Cargo[]>(this.api.get('chargeUsers')).subscribe(
        charges => {
          obs.next(charges);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
  *Retorna un login en especifico con us grupos a los que pertenece
  *
  * @param {number} id
  * @return {*}  {Promise<Login>}
  * @memberof SystemUsuersDataService
  */
   getUserLogin(id: number): Promise<Login> {
    return new Promise((resolve, reject) => {
      this.http.skipErrorHandler()
       .get<Login>(this.api.get('usersManagement', [id])).subscribe(
        user => {
          resolve(user);
        },
        err => {
          this.toastr.error('Lo sentimos ha ocurrido un error', 'Error de operación');
          reject(err);
        }
      );
    });
  }

  /**
  *Consulta las sedes de una empresa
  *
  * @param {number} idCompany
  * @return {*}  {Observable<Sede[]>}
  * @memberof SystemUsuersDataService
  */
   getSedes(idCompany: number): Observable<Sede[]> {
    return new Observable(obs => {
      this.http.get<Sede[]>(this.api.get('sedesManagement', [idCompany])).subscribe(
        sedes => {
          obs.next(sedes);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  deleteSelectedUser(id_user: number): Promise<boolean>{
    return new Promise((resolve, reject) => {
      this.http.delete<boolean>(this.api.get('adminUsers', [id_user])).subscribe(
        resp => {
          this.toastr.success('Operación exitosa', 'Usuario eliminado');
          resolve(true);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            if (err.status == 400 && typeof(err.error) === 'string'){
              this.toastr.warning(err.error, 'Error de operación')
            } else {
              this.toastr.error('Fallo del sistema', 'Error de operacion');
            }
          }
          reject(err);
        }
      );
    }
    )
  }

  createUser(user: any): Promise<boolean>{
    return new Promise ((resolve, reject) => {
      this.http.post<boolean>(this.api.get('adminUsers'), user).subscribe(
        resp => {
          this.toastr.success('Operación exitosa', 'Usuario creado');
          resolve(resp);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            if (err.error == 'El nombre de usuario ya existe') {
              this.toastr.warning("El nombre de usuario ya existe");
            } else if (err.error['permiso']) {
              this.toastr.warning('Fallo del sistema', 'Error de operacion')
            } else {
              // this.toastr.warning('Fallo del sistema', 'Error de operacion')
              this.toastr.warning(err.error, 'Error de operacion');
            }
          }
          reject(err);
        }
      )
    })
  }

  updateSelectedUser(id_user: number, user: any = {}): Promise<boolean>{
    return new Promise((resolve, reject) => {
      this.http.put<boolean>(this.api.get('adminUsers', [id_user]), user).subscribe(
        resp => {
          this.toastr.success('Operación exitosa', 'Usuario actualizado');
          resolve(true);
        },
        err => {
          if (err.status !== 401 && err.status !== 403) {
            if (err.status == 400 && typeof(err.error) === 'string'){
              this.toastr.warning(err.error, 'Error de operacion')
            }
            else if (err.error['permiso']) {
              this.toastr.error('Fallo del sistema', 'Error de operacion')
            } else {
              this.toastr.error('Fallo del sistema', 'Error de operacion');
            }
          }
          reject(err);
        }
      )
    }) 
  }

  /**
   * Consulta login automatico
   */
  automaticLogin(user: any = {}): Observable<Boolean>{
    return new Observable(obs => {
      this.http.post<Boolean>(this.api.get('autoNameUser'), user).subscribe(
        resp => {
          obs.next(resp)
        },
        err => {
          reject(err)
        }
      )
    })
    
  }
}



// WEBPACK FOOTER //
// ./src/app/administration/groups-permissions/system-users/shared/system-users.data.service.ts